import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendarAlt, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-form-datepicker',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormErrorDirective, FontAwesomeModule],
  template: `
    <div>
      <label [for]="id" class="block text-sm/6 font-medium text-gray-900">{{ label }}</label>
      <div class="relative">
        <div class="relative">
          <input
            #dateInput
            type="text"
            [id]="id"
            [formControl]="control"
            [placeholder]="placeholder"
            [readOnly]="true"
            (click)="toggleCalendar($event)"
            class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400
            focus:outline-1 focus:-outline-offset-1 focus:outline-focus/70 focus:shadow-sm sm:text-sm/6 transition duration-300 ease-in-out cursor-pointer pr-8"
            [ngClass]="{ 'outline-red-300': hasError }"
          />
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 pointer-events-none"
          >
            <fa-icon [icon]="faCalendarAlt" class="text-sm"></fa-icon>
          </button>
        </div>
        <span appFormError [errors]="customErrors || errors" [hidden]="!hasError" class="text-xs mt-1 text-red-400"></span>

        <!-- Calendario desplegable -->
        @if (showCalendar) {
          <div
            #calendar
            [class]="getCalendarPositionClass()"
          >
            <!-- Encabezado del calendario -->
            <div class="flex justify-between items-center mb-1">
              <button
                type="button"
                class="p-1 rounded-full hover:bg-gray-100"
                (click)="prevMonth()"
              >
                <fa-icon [icon]="faChevronLeft" class="text-gray-600 text-xs"></fa-icon>
              </button>
              <div class="font-medium text-sm">
                {{ currentMonthName }} {{ currentYear }}
              </div>
              <button
                type="button"
                class="p-1 rounded-full hover:bg-gray-100"
                (click)="nextMonth()"
              >
                <fa-icon [icon]="faChevronRight" class="text-gray-600 text-xs"></fa-icon>
              </button>
            </div>

            <!-- Días de la semana -->
            <div class="grid grid-cols-7 gap-0 text-center text-xs text-gray-500 mb-1">
              <div *ngFor="let day of weekDays" class="py-0.5 text-[10px]">{{ day }}</div>
            </div>

            <!-- Días del mes -->
            <div class="grid grid-cols-7 gap-0">
              @for (date of calendarDays; track date.month + '-' + date.day + '-' + date.year) {
                <button
                  type="button"
                  [class]="getDateClass(date)"
                  (click)="selectDate(date)"
                >
                  {{ date.day }}
                </button>
              }
            </div>

            <!-- Footer con botones -->
            <div class="flex justify-end mt-1 pt-1 border-t border-gray-200">
              <button
                type="button"
                class="px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100 rounded-md mr-1"
                (click)="clearDate()"
              >
                Limpiar
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-xs bg-primary text-white rounded-md hover:bg-primary-dark"
                (click)="todayDate()"
              >
                Hoy
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class FormInputDateComponent implements OnInit {
  private _control: FormControl = new FormControl();

  @Input() label!: string;
  @Input() id!: string;
  @Input() className = '';
  @Input() placeholder = 'Seleccionar fecha';
  @Input() customErrors: { [key: string]: string } = {};
  @Input() set control(value: AbstractControl<any, any> | null) {
    this._control = (value as FormControl) ?? new FormControl();
  }

  @ViewChild('dateInput') dateInput!: ElementRef;
  @ViewChild('calendar') calendarRef!: ElementRef;

  // Icons
  faCalendarAlt = faCalendarAlt;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;

  // Calendar state
  showCalendar = false;
  currentDate = new Date();
  currentMonth = this.currentDate.getMonth();
  currentYear = this.currentDate.getFullYear();
  selectedDate: Date | null = null;
  calendarDays: Array<{ day: number; month: number; year: number; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean }> = [];
  weekDays = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
  months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Determina si el calendario debe mostrarse arriba
  showAbove = false;

  get currentMonthName(): string {
    return this.months[this.currentMonth];
  }

  get control(): FormControl {
    return this._control;
  }

  get errors(): { [key: string]: string } {
    const controlErrors = this.control?.errors;
    if (!controlErrors) return {};

    return Object.keys(controlErrors).reduce((acc: { [key: string]: string }, key: string) => {
      acc[key] = this.customErrors[key] || 'Campo inválido';
      return acc;
    }, {});
  }

  get hasError(): boolean {
    return this.control?.invalid && (this.control?.touched || this.control?.dirty);
  }

  ngOnInit(): void {
    // Inicializar calendario y establecer fecha seleccionada si existe
    const controlValue = this.control.value;
    if (controlValue) {
      this.selectedDate = new Date(controlValue);
      this.updateInputValue();
    }
    this.generateCalendarDays();

    // Suscribirse a cambios del control
    this.control.valueChanges.subscribe(value => {
      if (value) {
        this.selectedDate = new Date(value);
        this.currentMonth = this.selectedDate.getMonth();
        this.currentYear = this.selectedDate.getFullYear();
        this.generateCalendarDays();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    // Cerrar el calendario cuando se hace clic fuera del componente
    if (this.showCalendar &&
        !this.calendarRef?.nativeElement.contains(event.target) &&
        !this.dateInput.nativeElement.contains(event.target)) {
      this.showCalendar = false;
    }
  }

  /**
   * Determina la posición del calendario (arriba o abajo) según el espacio disponible
   */
  getCalendarPositionClass(): string {
    const baseClass = 'z-10 p-2 bg-white rounded-md shadow-lg border border-gray-200 w-60 fixed';

    if (this.showAbove) {
      return `${baseClass} bottom-full mb-1`;
    } else {
      return `${baseClass} top-full mt-1`;
    }
  }

  toggleCalendar(event: MouseEvent): void {
    this.showCalendar = !this.showCalendar;

    if (this.showCalendar) {
      // Determinar si hay espacio suficiente abajo
      setTimeout(() => {
        if (this.dateInput && this.calendarRef) {
          const inputRect = this.dateInput.nativeElement.getBoundingClientRect();
          const calendarHeight = this.calendarRef.nativeElement.offsetHeight;
          const calendarWidth = this.calendarRef.nativeElement.offsetWidth;
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const spaceBelow = viewportHeight - inputRect.bottom;
          const spaceRight = viewportWidth - inputRect.left;

          // Si no hay suficiente espacio abajo, mostrar arriba
          this.showAbove = spaceBelow < calendarHeight + 10;

          // Posicionar el calendario
          const calendarEl = this.calendarRef.nativeElement;

          // Verificar si hay suficiente espacio a la derecha
          const alignLeft = spaceRight < calendarWidth + 10;

          if (alignLeft) {
            // Alinear a la derecha del input (abriendo hacia la izquierda)
            const rightEdge = inputRect.left + inputRect.width;
            calendarEl.style.left = 'auto';
            calendarEl.style.right = `${viewportWidth - rightEdge}px`;
          } else {
            // Alinear a la izquierda del input (abriendo hacia la derecha)
            calendarEl.style.left = `${inputRect.left}px`;
            calendarEl.style.right = 'auto';
          }

          if (this.showAbove) {
            calendarEl.style.bottom = `${viewportHeight - inputRect.top}px`;
            calendarEl.style.top = 'auto';
            calendarEl.style.marginBottom = '4px';
          } else {
            calendarEl.style.top = `${inputRect.bottom}px`;
            calendarEl.style.bottom = 'auto';
            calendarEl.style.marginTop = '4px';
          }
        }
      }, 0);

      if (this.selectedDate) {
        this.currentMonth = this.selectedDate.getMonth();
        this.currentYear = this.selectedDate.getFullYear();
        this.generateCalendarDays();
      }
    }
  }

  prevMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendarDays();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendarDays();
  }

  selectDate(date: { day: number; month: number; year: number }): void {
    const now = new Date();
    this.selectedDate = new Date(date.year, date.month, date.day, now.getHours(), now.getMinutes());
    this.updateInputValue();
    this.showCalendar = false;
    this.generateCalendarDays();
  }

  clearDate(): void {
    this.selectedDate = null;
    this.control.setValue(null);
    this.showCalendar = false;
  }

  todayDate(): void {
    const today = new Date();
    this.selectedDate = today;
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.updateInputValue();
    this.generateCalendarDays();
    this.showCalendar = false;
  }

  updateInputValue(): void {
    if (this.selectedDate) {
      // Formato yyyy-MM-dd para compatibilidad con el control
      const year = this.selectedDate.getFullYear();
      const month = String(this.selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.selectedDate.getDate()).padStart(2, '0');
      const hours = String(this.selectedDate.getHours()).padStart(2, '0');
      const minutes = String(this.selectedDate.getMinutes()).padStart(2, '0');

      // Mantener el formato ISO para el valor del control
      this.control.setValue(`${year}-${month}-${day}T${hours}:${minutes}`);

      // Formato localizado para mostrar al usuario
      this.dateInput.nativeElement.value = `${day}/${month}/${year} ${hours}:${minutes}`;
    }
  }

  /**
   * Genera los días del calendario para el mes actual
   */
  generateCalendarDays(): void {
    this.calendarDays = [];

    // Primer día del mes actual
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
    // Último día del mes actual
    const lastDayOfMonth = new Date(this.currentYear, this.currentMonth + 1, 0);

    // Índice del primer día de la semana (0 = Domingo, 1 = Lunes, etc)
    const firstDayIndex = firstDayOfMonth.getDay();

    // Días del mes anterior para completar la primera semana
    if (firstDayIndex > 0) {
      const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();
      for (let i = 0; i < firstDayIndex; i++) {
        const prevMonth = this.currentMonth === 0 ? 11 : this.currentMonth - 1;
        const prevYear = this.currentMonth === 0 ? this.currentYear - 1 : this.currentYear;
        const day = prevMonthLastDay - firstDayIndex + i + 1;

        this.calendarDays.push({
          day,
          month: prevMonth,
          year: prevYear,
          isCurrentMonth: false,
          isToday: this.isToday(day, prevMonth, prevYear),
          isSelected: this.isSelected(day, prevMonth, prevYear)
        });
      }
    }

    // Días del mes actual
    const today = new Date();
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      this.calendarDays.push({
        day,
        month: this.currentMonth,
        year: this.currentYear,
        isCurrentMonth: true,
        isToday: this.isToday(day, this.currentMonth, this.currentYear),
        isSelected: this.isSelected(day, this.currentMonth, this.currentYear)
      });
    }

    // Días del mes siguiente para completar la última semana
    const daysToAdd = 42 - this.calendarDays.length; // 6 semanas * 7 días
    if (daysToAdd > 0) {
      for (let day = 1; day <= daysToAdd; day++) {
        const nextMonth = this.currentMonth === 11 ? 0 : this.currentMonth + 1;
        const nextYear = this.currentMonth === 11 ? this.currentYear + 1 : this.currentYear;

        this.calendarDays.push({
          day,
          month: nextMonth,
          year: nextYear,
          isCurrentMonth: false,
          isToday: this.isToday(day, nextMonth, nextYear),
          isSelected: this.isSelected(day, nextMonth, nextYear)
        });
      }
    }
  }

  /**
   * Verifica si una fecha es hoy
   */
  isToday(day: number, month: number, year: number): boolean {
    const today = new Date();
    return day === today.getDate() &&
           month === today.getMonth() &&
           year === today.getFullYear();
  }

  /**
   * Verifica si una fecha es la seleccionada
   */
  isSelected(day: number, month: number, year: number): boolean {
    if (!this.selectedDate) return false;

    return day === this.selectedDate.getDate() &&
           month === this.selectedDate.getMonth() &&
           year === this.selectedDate.getFullYear();
  }

  /**
   * Obtiene las clases CSS para un día del calendario
   */
  getDateClass(date: { day: number; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean }): string {
    const baseClass = 'w-7 h-7 rounded-full flex items-center justify-center text-xs transition-colors duration-200';

    if (date.isSelected) {
      return `${baseClass} bg-primary text-white`;
    }

    if (date.isToday) {
      return `${baseClass} bg-primary-light text-primary font-bold`;
    }

    if (date.isCurrentMonth) {
      return `${baseClass} hover:bg-gray-100 text-gray-700`;
    }

    return `${baseClass} text-gray-400 hover:bg-gray-50`;
  }
}
